/**
 * Farm rules made quick to switch on and off (packages/core/src/rules.ts). A farmer with 10
 * pumps and 4 irrigators pairs them differently from day to day: today pumps 1–3 follow
 * irrigator 2. So beside every output there's a list of the rules that switch it, each with
 * an on/off switch, and a one-line way to make "switch these off when that switches off".
 * The Rules tab on the Programs page lists every rule by the output it switches.
 *
 * Turning a rule on while what it watches is already in that state (irrigator 2 isn't running
 * yet) asks first: act now, or wait until it changes. Nothing is switched by the app itself;
 * the program runner does it, and only shows it as done once the board confirms.
 */
import { useEffect, useState } from "react";
import { programsPollMs } from "../programs-text.ts";
import { api, ApiError, type Device, type ProgramView } from "../api.ts";
import { boardPorts, can } from "../control.ts";
import { ago, deviceStatus, isFmsBoard, isOnOff, portKey, portLabel } from "../format.ts";
import { Problem, Spinner, useLoad } from "../ui.tsx";
import { Toggle } from "../widgets.tsx";
import { isRule, outputsOf, quickRuleProgram, ruleSummary, switchesOutput, type QuickRule } from "../../../../packages/core/src/rules.ts";

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

type Label = (devEui: string, port: string) => string;

function labeller(devices: Device[]): Label {
  return (eui, port) => {
    const d = devices.find((x) => x.devEui === eui);
    return d ? `${d.name} ${portLabel(d, port)}` : port;
  };
}

/** On/off things a rule can watch: inputs and outputs that report on or off. */
function watchable(devices: Device[]) {
  return devices.flatMap((d) =>
    d.ports
      .filter((p) => p.enabled && isOnOff(d, portKey(p)))
      .map((p) => ({ devEui: d.devEui, port: portKey(p), text: `${d.name} · ${portLabel(d, portKey(p))}` })),
  );
}

/** Outputs a rule can switch: FMS board outputs (the ones that confirm commands). */
function switchable(devices: Device[]) {
  return devices
    .filter((d) => isFmsBoard(d.model))
    .flatMap((d) =>
      boardPorts(d.model)
        .filter((p) => (p.kind === "ro" || p.kind === "do") && d.ports.some((x) => x.enabled && portKey(x) === `${p.kind}${p.channel}`))
        .map((p) => ({ devEui: d.devEui, kind: p.kind as "ro" | "do", channel: p.channel, text: `${d.name} · ${portLabel(d, `${p.kind}${p.channel}`)}` })),
    );
}

// ------------------------------------------------------------------ one rule's switch

export function RuleRow({ v, label, canSwitch, onChanged }: { v: ProgramView; label: Label; canSwitch: boolean; onChanged: () => void }) {
  const p = v.program;
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  const set = async (on: boolean, wait?: boolean) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.enableProgram(p.id, on, wait);
      if (r.needsChoice) setAsk(r.trueNow ?? []);
      else {
        setAsk(null);
        onChanged();
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const last = [...v.run.log].reverse().find((l) => /^Start/i.test(l.text) || /started/i.test(l.text));
  const running = v.run.state === "running" || v.run.state === "stopping";
  return (
    <div className="rule-row">
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="t">{p.name}</div>
        <div className="d">{ruleSummary(p, label)}</div>
        <div className="d">
          {running ? <span className="pill info">Running now</span> : null}
          {p.offAfterRun ? " Turns itself off after it runs." : ""}
          {last ? ` Last ran ${ago(last.at, Date.now())}.` : ""}
          {p.note ? <span className="muted"> {p.note}</span> : null}
        </div>
        {ask && (
          <div className="rule-ask">
            <div>
              <b>{ask.join(" and ")} right now.</b> Turned on as it is, this rule would act straight away.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              <button className="btn primary sm" disabled={busy} onClick={() => void set(true, true)}>
                Wait until it changes
              </button>
              <button className="btn sm" disabled={busy} onClick={() => void set(true, false)}>
                Act now
              </button>
              <button className="btn ghost sm" onClick={() => setAsk(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {error && <div className="error small">{error}</div>}
      </div>
      <Toggle on={p.enabled} pending={busy} disabled={!canSwitch || busy} label={`${p.name}: ${p.enabled ? "turn off" : "turn on"}`} onChange={(on) => void set(on)} />
    </div>
  );
}

// ------------------------------------------------------------------ making a quick rule

export function QuickRuleForm({ devices, accountId, preset, onDone, onCancel }: { devices: Device[]; accountId?: string | undefined; preset?: { devEui: string; key: string }; onDone: () => void; onCancel: () => void }) {
  const watch = watchable(devices);
  const outs = switchable(devices);
  const [w, setW] = useState("");
  const [when, setWhen] = useState<"off" | "on">("off");
  const [forSec, setForSec] = useState("10");
  const [turn, setTurn] = useState<"off" | "on">("off");
  const [picked, setPicked] = useState<string[]>(preset ? [`${preset.devEui}:${preset.key}`] : []);
  // A rule usually protects something ("pumps off when the irrigator stops"): it stays on unless
  // the farmer asks for a one-off. Defaulting to one-off quietly disarmed it after its first run.
  const [once, setOnce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const label = labeller(devices);
  const ok = w && picked.length && Number(forSec) >= 0;
  // Watching a device that isn't reporting: the rule can't see it switch, so it would never act.
  const watched = w ? devices.find((d) => d.devEui === w.split(":")[0]) : undefined;
  const watchedQuiet = watched && ["offline", "never"].includes(deviceStatus(watched, Date.now())) ? watched : undefined;
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const [devEui, port] = w.split(":");
      const q: QuickRule = {
        watch: { devEui: devEui!, port: port! },
        when,
        forSec: Math.round(Number(forSec) || 0),
        outputs: outs.filter((o) => picked.includes(`${o.devEui}:${o.kind}${o.channel}`)).map((o) => ({ devEui: o.devEui, kind: o.kind, channel: o.channel })),
        turn,
        offAfterRun: once,
      };
      // "When it switches off" means the next time it does: if it's already off, it waits.
      await api.createRule(quickRuleProgram(q, label, "Australia/Brisbane"), { ...(accountId ? { accountId } : {}), waitForChange: true });
      onDone();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  // Outputs grouped by device, so 10 pumps across a few boards are easy to tick.
  const byDevice = new Map<string, typeof outs>();
  for (const o of outs) byDevice.set(o.devEui, [...(byDevice.get(o.devEui) ?? []), o]);
  return (
    <div className="quick-rule">
      <div className="qr-line">
        <span>Switch</span>
        <select value={turn} onChange={(e) => setTurn(e.target.value as "on" | "off")} aria-label="Switch on or off">
          <option value="off">off</option>
          <option value="on">on</option>
        </select>
        <span>{picked.length ? `${picked.length} output${picked.length === 1 ? "" : "s"}` : "these outputs"} when</span>
        <select value={w} onChange={(e) => setW(e.target.value)} aria-label="What to watch" style={{ minWidth: 200 }}>
          <option value="">choose what to watch…</option>
          {watch.map((x) => (
            <option key={`${x.devEui}:${x.port}`} value={`${x.devEui}:${x.port}`}>
              {x.text}
            </option>
          ))}
        </select>
        <span>switches</span>
        <select value={when} onChange={(e) => setWhen(e.target.value as "on" | "off")} aria-label="Switches on or off">
          <option value="off">off</option>
          <option value="on">on</option>
        </select>
        <span>for</span>
        <input className="num-in" style={{ width: 60 }} inputMode="numeric" value={forSec} onChange={(e) => setForSec(e.target.value)} aria-label="Seconds it must hold" />
        <span>s</span>
      </div>
      <div className="qr-outs">
        {[...byDevice].map(([eui, list]) => (
          <div key={eui} className="qr-dev">
            <div className="muted small">{devices.find((d) => d.devEui === eui)?.name}</div>
            <div className="chips">
              {list.map((o) => {
                const k = `${o.devEui}:${o.kind}${o.channel}`;
                const on = picked.includes(k);
                return (
                  <button key={k} type="button" className="chip" aria-pressed={on} onClick={() => setPicked(on ? picked.filter((x) => x !== k) : [...picked, k])}>
                    {o.text.split(" · ")[1]}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!outs.length && <div className="muted small">No board outputs are set up yet.</div>}
      </div>
      <label className="checkline">
        <input type="checkbox" checked={once} onChange={(e) => setOnce(e.target.checked)} />
        <span>One-off: turn this rule off after it has run once (for today's pairing; turn it on again any time)</span>
      </label>
      {watchedQuiet && (
        <div className="banner warn small">
          {watchedQuiet.name} {watchedQuiet.lastSeenAt ? `hasn't been heard for ${ago(watchedQuiet.lastSeenAt, Date.now()).replace(" ago", "")}` : "has never been heard"}. The rule only acts when it hears it switch.
        </div>
      )}
      {error && <div className="error small">{error}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn primary" disabled={!ok || busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Make the rule and turn it on"}
        </button>
        <button className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="muted small">If what it watches is already {when} now, the rule waits for it to switch {when === "off" ? "on and then off" : "off and then on"} again.</div>
    </div>
  );
}

// ------------------------------------------------------------------ beside an output

/** The rules that switch one output, each with its own switch, and a quick way to add one. */
export function OutputRules({ device, outputKey, role, staff, onChanged }: { device: Device; outputKey: string; role: string; staff: boolean; onChanged?: () => void }) {
  const acct = staff ? device.accountId : undefined;
  const [poll, setPoll] = useState(30_000);
  const list = useLoad(() => api.programs(acct), [acct], poll);
  useEffect(() => setPoll(programsPollMs(list.data?.programs, 5_000)), [list.data]);
  const devices = useLoad(() => api.devices(acct), [acct]);
  const [adding, setAdding] = useState(false);
  if (list.error && !list.data) return <Problem error={list.error} onRetry={list.reload} />;
  if (!list.data || !devices.data) return <Spinner />;
  const label = labeller(devices.data.devices);
  const rules = list.data.programs.filter((v) => isRule(v.program) && switchesOutput(v.program, device.devEui, outputKey));
  const canSwitch = can(role, "control");
  const canMake = can(role, "edit-rules");
  return (
    <div className="output-rules">
      <div className="muted small">
        Rules that switch <b>{portLabel(device, outputKey)}</b>
        {rules.length ? ` · ${rules.filter((r) => r.program.enabled).length} of ${rules.length} on` : ""}
        {!list.data.running ? " · they only run once Think-Control runs this farm" : ""}
      </div>
      {rules.map((v) => (
        <RuleRow
          key={v.program.id}
          v={v}
          label={label}
          canSwitch={canSwitch}
          onChanged={() => {
            list.reload();
            onChanged?.();
          }}
        />
      ))}
      {!rules.length && !adding && <div className="muted small">No rules switch this output yet.</div>}
      {canMake &&
        (adding ? (
          <QuickRuleForm
            devices={devices.data.devices}
            accountId={acct}
            preset={{ devEui: device.devEui, key: outputKey }}
            onDone={() => {
              setAdding(false);
              list.reload();
              onChanged?.();
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button className="btn ghost sm" style={{ alignSelf: "flex-start" }} onClick={() => setAdding(true)}>
            + Switch this when something else switches
          </button>
        ))}
    </div>
  );
}

/** How many rules switch this output and how many are on, for the output's row. */
export function rulesCount(programs: ProgramView[] | undefined, devEui: string, key: string): { on: number; total: number } {
  const rules = (programs ?? []).filter((v) => isRule(v.program) && switchesOutput(v.program, devEui, key));
  return { on: rules.filter((v) => v.program.enabled).length, total: rules.length };
}

// ------------------------------------------------------------------ Rules tab

/** Every rule on the farm, grouped by the output it switches, each with its switch. */
export function RulesList({ programs, devices, accountId, role, onChanged }: { programs: ProgramView[]; devices: Device[]; accountId?: string | undefined; role: string; onChanged: () => void }) {
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const label = labeller(devices);
  const rules = programs.filter((v) => isRule(v.program));
  const f = filter.trim().toLowerCase();
  const shown = rules.filter((v) => !f || v.program.name.toLowerCase().includes(f) || ruleSummary(v.program, label).toLowerCase().includes(f));
  // Grouped by the first output each rule switches; a rule for pumps 1–3 shows under each.
  const groups = new Map<string, ProgramView[]>();
  for (const v of shown) {
    const outs = outputsOf(v.program);
    for (const o of outs.length ? outs : ["none"]) groups.set(o, [...(groups.get(o) ?? []), v]);
  }
  const groupName = (k: string) => {
    if (k === "none") return "Switches nothing yet";
    const [eui, key] = k.split(":");
    return label(eui!, key!);
  };
  const canMake = can(role, "edit-rules");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="card pad" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div className="field" style={{ flex: "1 1 220px" }}>
          <input placeholder="Find a rule (pump, irrigator, tank…)" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find a rule" />
        </div>
        <span className="muted small">
          {rules.filter((v) => v.program.enabled).length} of {rules.length} rules on
        </span>
        {canMake && !adding && (
          <button className="btn primary" onClick={() => setAdding(true)}>
            + Quick rule
          </button>
        )}
      </div>
      {adding && (
        <div className="card pad">
          <QuickRuleForm
            devices={devices}
            accountId={accountId}
            onDone={() => {
              setAdding(false);
              onChanged();
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
      {!rules.length && <div className="card empty">No rules yet. A rule switches outputs when a reading changes, e.g. pumps off when an irrigator stops.</div>}
      {[...groups]
        .sort((a, b) => groupName(a[0]).localeCompare(groupName(b[0])))
        .map(([k, list]) => (
          <div key={k} className="card">
            <div className="pad" style={{ paddingBottom: 0 }}>
              <b>{groupName(k)}</b>
              <span className="muted small"> · {list.filter((v) => v.program.enabled).length} of {list.length} on</span>
            </div>
            <div className="list">
              {list.map((v) => (
                <RuleRow key={v.program.id} v={v} label={label} canSwitch={can(role, "control")} onChanged={onChanged} />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
